import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Image } from 'react-bootstrap';
import { FaMicrosoft } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAuthStatus } from '../api/auth';
import axios from 'axios';

// Logo styling
const logoStyle = {
  height: '45px',
  marginBottom: '15px'
};

// Page container style for centering (matching ContactSearch)
const pageContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-7vh' // Move content higher on the page
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    console.log("Login component mounted/updated", { 
      path: location.pathname,
      search: location.search,
      isRedirecting
    });
    
    // Don't do anything if already redirecting
    if (isRedirecting) {
      console.log("Already redirecting, skipping additional processing");
      return;
    }
    
    // Parse URL parameters
    const params = new URLSearchParams(location.search);
    const authSuccess = params.get('auth_success');
    const errorMsg = params.get('error');
    
    // Handle auth_success parameter first (coming from server callback)
    if (authSuccess === 'true') {
      console.log("Auth success detected in URL, checking for redirect URL");
      setIsRedirecting(true);
      handleSuccessfulAuth();
      return;
    }
    
    // Handle error message in URL
    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      // Clear the error from the URL to prevent it from persisting on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // If not handling auth_success and not redirecting, check auth status
    checkAuthStatus()
      .then(response => {
        if (response.isAuthenticated) {
          console.log("User is already authenticated");
          setIsRedirecting(true);
          handleSuccessfulAuth();
        }
      })
      .catch(error => {
        console.error('Error checking authentication status:', error);
      });
  }, [location, navigate]);
  
  // Handle successful authentication (either from auth_success or checkAuthStatus)
  const handleSuccessfulAuth = () => {
    const redirectUrl = localStorage.getItem('redirectUrl');
    console.log("handleSuccessfulAuth called, redirectUrl:", redirectUrl);
    
    // Clear the auth_success parameter from URL to avoid infinite loops
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('auth_success')) {
      console.log("Removing auth_success from URL");
      currentUrl.searchParams.delete('auth_success');
      window.history.replaceState({}, document.title, currentUrl.toString());
    }
    
    // Add a short timeout to ensure state is fully updated
    setTimeout(() => {
      if (redirectUrl) {
        console.log("Found redirect URL in localStorage:", redirectUrl);
        
        try {
          // Validate the URL is within our app
          const redirectTo = new URL(redirectUrl, window.location.origin);
          
          // Clear local storage first to prevent redirect loops
          localStorage.removeItem('redirectUrl');
          
          // Log a final message before redirect
          console.log("Redirecting to:", redirectTo.href);
          
          // Use navigate instead of window.location for a smoother experience
          navigate(redirectTo.pathname + redirectTo.search);
        } catch (e) {
          console.error("Invalid redirect URL:", e);
          navigate('/');
        }
      } else {
        console.log("No redirect URL found, going to home page");
        navigate('/');
      }
    }, 100);
  };

  const handleMicrosoftLogin = async () => {
    try {
      setError(null); // Clear any previous errors
      // Get the Microsoft auth URL from the server
      const response = await axios.get('/api/auth/microsoft-url');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error redirecting to Microsoft auth:', error);
      setError('Unable to connect to authentication service. Please try again later.');
    }
  };

  return (
    <Container style={pageContainerStyle}>
      <Row className="justify-content-center w-100">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <Image 
                  src="/crestmontlogo.webp" 
                  alt="Crestmont Logo" 
                  style={logoStyle} 
                  className="mx-auto"
                />
              </div>
              
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}
              
              <Button 
                variant="outline-primary" 
                size="lg" 
                className="w-100 d-flex align-items-center justify-content-center gap-2 mb-3"
                onClick={handleMicrosoftLogin}
              >
                <FaMicrosoft />
                <span>Sign in with Microsoft</span>
              </Button>
              
              <div className="mt-4 text-center">
                <small className={error ? "text-danger fw-bold" : "text-muted"}>
                  You must be an authorized user to access this application.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
